import {
  ActivityTimeline,
  Button,
  formatDate,
  formatDateTime,
  formatMoney,
  isoDay,
  ListView,
  PageEmpty,
  PageError,
  PageLoading,
  pickerDay,
  useCursorList,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { DateRangePicker } from "@majitap/ui/arc/date-range-picker/date-range-picker";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useActivity } from "../../activity";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { isAmount, type Owner, paymentTermsQuery, type WalletOwner, walletOfQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { ORDER_STATUS, PAYMENT_STATUS } from "../../labels";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import styles from "../customers/customers.module.css";
import { PayerAgeing } from "./finance";

/**
 * Panels a customer's and a business's pages share: their orders, wallet, credit and history.
 * Each takes the owner; the API paths differ by kind, nothing else does.
 */

export function OrdersPanel({ owner }: { owner: Owner }): ReactNode {
  const format = useCompanyFormat();
  const list = useCursorList({
    queryKey: ["orders", owner],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/orders", { params: { query: { ...owner, cursor, limit: 10 } } });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.muted}>Newest first</span>
        <span className={styles.actions}>
          <Link to="/pricing" search={{ ...owner, when: "any" }} className={styles.link}>
            Their prices
          </Link>
          <Link to="/orders" search={owner} className={styles.link}>
            Open in Orders
          </Link>
        </span>
      </div>
      <ListView
        caption="Orders"
        itemName={{ one: "order", other: "orders" }}
        rows={list.rows}
        rowKey={(o) => o.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={{ title: "No orders yet", description: "Orders placed for them show here." }}
        columns={[
          {
            key: "order_number",
            label: "Order",
            render: (o) => (
              <Link to="/orders/$orderId" params={{ orderId: o.id }} className={styles.link}>
                {o.order_number}
              </Link>
            ),
          },
          { key: "location", label: "Place", render: (o) => o.location?.label ?? "—" },
          { key: "total_amount", label: "Total", numeric: true, render: (o) => formatMoney(o.total_amount, format) },
          {
            key: "payment_status",
            label: "Payment",
            render: (o) => (
              <Badge tone={PAYMENT_STATUS[o.payment_status][1]}>{PAYMENT_STATUS[o.payment_status][0]}</Badge>
            ),
          },
          {
            key: "status",
            label: "Status",
            render: (o) => <Badge tone={ORDER_STATUS[o.status][1]}>{ORDER_STATUS[o.status][0]}</Badge>,
          },
          { key: "created_at", label: "Placed", render: (o) => formatDateTime(o.created_at, format) },
        ]}
      />
    </div>
  );
}

export function WalletPanel({ owner }: { owner: WalletOwner }): ReactNode {
  const format = useCompanyFormat();
  const wallet = useQuery(walletOfQuery(owner));
  if (wallet.status === "pending") return <PageLoading label="Loading the wallet" />;
  if (wallet.status === "error")
    return <PageError message={messageFor(errorOf(wallet.error))} onRetry={() => wallet.refetch()} />;
  if (!wallet.data)
    return <PageEmpty title="No wallet yet" description="A wallet opens with the first payment into it." />;
  const w = wallet.data;
  const money = (v: string) => formatMoney(v, { ...format, currency: w.currency });
  return (
    <div className={styles.panel}>
      <div className={styles.numbers}>
        {[
          ["Balance", w.balance],
          ["Held", w.held_amount],
          ["Available", w.available],
          ["Credit limit", w.credit_limit],
        ].map(([label, value]) => (
          <div key={label} className={styles.number}>
            <span className={styles.muted}>{label}</span>
            <strong>{money(value)}</strong>
          </div>
        ))}
      </div>
      <div className={styles.head}>
        <span className={styles.muted}>{w.status === "active" ? "Active" : `Wallet ${w.status}`}</span>
        <RouterLink href={`/wallets/${w.id}`} className={styles.link}>
          Statement and changes in Wallets
        </RouterLink>
      </div>
    </div>
  );
}

type CreditOwner = { kind: "customer"; id: string } | { kind: "business"; id: string };

async function creditOf(owner: CreditOwner) {
  const { data, error } =
    owner.kind === "customer"
      ? await api.GET("/v1/admin/billing/customers/{id}/credit", { params: { path: { id: owner.id } } })
      : await api.GET("/v1/admin/billing/businesses/{id}/credit", { params: { path: { id: owner.id } } });
  if (error || !data) throw errorOf(error);
  return data;
}

async function setTerm(owner: CreditOwner, payment_term_id: string | null) {
  const { error } =
    owner.kind === "customer"
      ? await api.PUT("/v1/admin/billing/customers/{id}/payment-term", {
          params: { path: { id: owner.id } },
          body: { payment_term_id },
        })
      : await api.PUT("/v1/admin/billing/businesses/{id}/payment-term", {
          params: { path: { id: owner.id } },
          body: { payment_term_id },
        });
  if (error) throw errorOf(error);
}

async function setLimit(owner: CreditOwner, credit_limit: string, reason: string) {
  const { error } =
    owner.kind === "customer"
      ? await api.PUT("/v1/admin/billing/customers/{id}/credit-limit", {
          params: { path: { id: owner.id } },
          body: { credit_limit, reason },
        })
      : await api.PUT("/v1/admin/billing/businesses/{id}/credit-limit", {
          params: { path: { id: owner.id } },
          body: { credit_limit, reason },
        });
  if (error) throw errorOf(error);
}

/** What they owe, on what terms, and how much more they can order on credit (invoices.issue). */
export function CreditPanel({ owner, paymentTermId }: { owner: CreditOwner; paymentTermId: string | null }): ReactNode {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  // the limit's route needs wallets.manage, which the Credit tab's invoices.issue doesn't give
  const canSetLimit = me?.permissions.includes("wallets.manage") ?? false;
  const credit = useQuery({ queryKey: ["credit", owner], queryFn: () => creditOf(owner) });
  const terms = useQuery(paymentTermsQuery);
  const [limit, setLimitDraft] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["credit", owner] }),
      queryClient.invalidateQueries({ queryKey: [owner.kind, owner.id] }),
    ]);
  const term = useMutation({
    mutationFn: (id: string) => setTerm(owner, id === "default" ? null : id),
    onSuccess: refresh,
  });
  const saveLimit = useMutation({
    mutationFn: () => setLimit(owner, (limit ?? "").trim(), reason.trim()),
    onSuccess: async () => {
      await refresh();
      setLimitDraft(null);
      setReason("");
    },
  });
  if (credit.status === "pending") return <PageLoading label="Loading credit" />;
  if (credit.status === "error")
    return <PageError message={messageFor(errorOf(credit.error))} onRetry={() => credit.refetch()} />;
  const k = credit.data;
  const money = (v: string) => formatMoney(v, { ...format, currency: k.currency });
  const defaultTerm = terms.data?.find((t) => t.is_default);
  return (
    <div className={styles.panel}>
      <div className={styles.numbers}>
        {[
          ["Owes", k.receivable],
          ["Ordered, not delivered", k.undelivered],
          ["Wallet", k.wallet_balance],
          ["Credit limit", k.credit_limit],
          ["Owed and on order", k.exposure],
          ["Can still order", k.available],
        ].map(([label, value]) => (
          <div key={label} className={styles.number}>
            <span className={styles.muted}>{label}</span>
            <strong>{money(value)}</strong>
          </div>
        ))}
      </div>
      <div className={styles.facts}>
        <div className={styles.form}>
          <Select
            label="Payment terms"
            value={paymentTermId ?? "default"}
            onValueChange={(v) => term.mutate(v)}
            options={[
              {
                value: "default",
                label: defaultTerm ? `The company's default (${defaultTerm.name})` : "The company's default",
              },
              ...(terms.data ?? []).filter((t) => t.is_active).map((t) => ({ value: t.id, label: t.name })),
            ]}
            description={
              k.on_credit
                ? "On credit: orders are billed later, up to the limit."
                : "Not on credit: orders are paid before or on delivery."
            }
          />
          {term.error ? (
            <Alert tone="danger" title="Terms not changed">
              {messageFor(errorOf(term.error))}
            </Alert>
          ) : null}
          {!canSetLimit ? null : limit === null ? (
            <Button variant="secondary" onClick={() => setLimitDraft(k.credit_limit)}>
              Change the credit limit
            </Button>
          ) : (
            <>
              <div className={styles.pair}>
                <Input
                  label={`Credit limit (${k.currency})`}
                  inputMode="decimal"
                  value={limit}
                  onChange={(e) => setLimitDraft(e.target.value)}
                  error={limit && !isAmount(limit) ? "An amount like 50000 or 50000.00." : undefined}
                />
                <Input
                  label="Reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  description="Recorded with the change."
                />
              </div>
              {saveLimit.error ? (
                <Alert tone="danger" title="Limit not changed">
                  {messageFor(errorOf(saveLimit.error))}
                </Alert>
              ) : null}
              <div className={styles.footer}>
                <Button variant="secondary" onClick={() => setLimitDraft(null)}>
                  Cancel
                </Button>
                <Button
                  disabled={!isAmount(limit) || !reason.trim()}
                  loading={saveLimit.isPending}
                  onClick={() => saveLimit.mutate()}
                >
                  Save limit
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <PayerAgeing owner={owner} />
      <StatementSection owner={owner} />
    </div>
  );
}

/** This month so far: the statement's starting period. */
export function defaultStatementPeriod(today: string): { from: string; to: string } {
  return { from: `${today.slice(0, 8)}01`, to: today };
}

/** The statement as a CSV for a period; the API needs both days (at most 366 days apart). */
export const statementCsvHref = (owner: CreditOwner, period: { from: string; to: string }) =>
  `/v1/admin/billing/${owner.kind === "customer" ? "customers" : "businesses"}/${owner.id}/statement?${new URLSearchParams(
    { ...period, format: "csv" },
  )}`;

/** "2026-10-01" as a picker's Date (local midnight). */
const pickerDate = (day: string) => new Date(`${day}T00:00:00`);

/** A statement of account for a chosen period: opening balance, every charge and payment, closing balance. */
function StatementSection({ owner }: { owner: CreditOwner }) {
  const format = useCompanyFormat();
  const [period, setPeriod] = useState(() => defaultStatementPeriod(isoDay(format.timezone)));
  const statement = useQuery({
    queryKey: ["statement", owner, period],
    queryFn: async () => {
      const { data, error } =
        owner.kind === "customer"
          ? await api.GET("/v1/admin/billing/customers/{id}/statement", {
              params: { path: { id: owner.id }, query: { ...period, format: "json" } },
            })
          : await api.GET("/v1/admin/billing/businesses/{id}/statement", {
              params: { path: { id: owner.id }, query: { ...period, format: "json" } },
            });
      if (error || !data) throw errorOf(error);
      // the same route answers CSV for format=csv; JSON was asked for
      if (typeof data === "string") throw new Error("The statement came back as CSV");
      return data;
    },
  });
  const s = statement.data;
  const money = (v: string) => (s ? formatMoney(v, { ...format, currency: s.currency }) : v);
  return (
    <section className={styles.form}>
      <div className={styles.head}>
        <h3 className={styles.title}>Statement</h3>
        <a className={styles.link} href={statementCsvHref(owner, period)} download>
          Download (CSV)
        </a>
      </div>
      <DateRangePicker
        label="Period"
        value={{ start: pickerDate(period.from), end: pickerDate(period.to) }}
        onChange={(range) => {
          if (range) setPeriod({ from: pickerDay(range.start), to: pickerDay(range.end) });
        }}
      />
      {statement.status === "pending" ? (
        <PageLoading label="Loading the statement" />
      ) : statement.status === "error" ? (
        <PageError message={messageFor(errorOf(statement.error))} onRetry={() => statement.refetch()} />
      ) : s ? (
        <>
          <div className={styles.numbers}>
            {[
              ["Owed at the start", s.opening_balance],
              ["Charged", s.total_debits],
              ["Paid or credited", s.total_credits],
              ["Owed at the end", s.closing_balance],
            ].map(([label, value]) => (
              <div key={label} className={styles.number}>
                <span className={styles.muted}>{label}</span>
                <strong>{money(value)}</strong>
              </div>
            ))}
          </div>
          {s.entries.length === 0 ? (
            <p className={styles.muted}>Nothing charged or paid in this period.</p>
          ) : (
            <ListView
              caption="Statement"
              itemName={{ one: "entry", other: "entries" }}
              rows={s.entries}
              rowKey={(e) => e.id}
              status="success"
              hasMore={false}
              loadingMore={false}
              onLoadMore={() => {}}
              filtered={false}
              empty={{ title: "Nothing in this period", description: "" }}
              columns={[
                { key: "date", label: "Date", render: (e) => formatDate(e.date, format) },
                {
                  key: "description",
                  label: "What",
                  render: (e) => (
                    <span className={styles.stack}>
                      <span>{e.description}</span>
                      <span className={styles.muted}>
                        {[e.invoice_number, e.order_number, e.delivery_number, e.payment_reference]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  ),
                },
                { key: "debit", label: "Charged", render: (e) => (e.debit === "0.00" ? "" : money(e.debit)) },
                { key: "credit", label: "Paid", render: (e) => (e.credit === "0.00" ? "" : money(e.credit)) },
                { key: "balance", label: "Owed", render: (e) => money(e.balance) },
              ]}
            />
          )}
        </>
      ) : null}
    </section>
  );
}

export function ActivityPanel({ targetType, id, label }: { targetType: string; id: string; label: string }): ReactNode {
  const format = useCompanyFormat();
  const activity = useActivity(targetType, id);
  if (activity.status === "pending") return <PageLoading label="Loading activity" />;
  if (activity.status === "error")
    return <PageError message={messageFor(errorOf(activity.error))} onRetry={() => activity.refetch()} />;
  return <ActivityTimeline entries={activity.data} timeZone={format.timezone} label={label} />;
}
